// Give the focus back to whoever opened the overlay.
//
// ## The defect
//
// A controlled `Dialog.Root` has no `Dialog.Trigger`, so Radix has nowhere to
// restore the focus to. The sheet closes, and the focus lands on `<body>` — a
// keyboard user has to start the whole page over. Measured on every one of the
// 20 `BottomSheet` call sites (and the 9 `ConfirmDialog` ones that inherit it).
//
// Radix *does* have a `FocusScope` that remembers the previously focused
// element, but it restores to that element **only if it is still in the
// document** and, in the dialog case, the `DialogContentModal` composes
// `onCloseAutoFocus` to focus the *trigger* — which does not exist here. So
// the net result is `<body>`.
//
// ## Why it has to happen during render
//
// Radix moves focus into the dialog in an **effect**, and React runs a child's
// effects before its parent's — so by the time an effect in the overlay's own
// component runs, `document.activeElement` is already the dialog and the
// element the user came from is gone. A `useEffect` cannot capture it.
//
// During render it is still the trigger: the parent's render happens before
// any effect runs. So the capture belongs there.
//
// The write is to a ref, which is the one kind of mutation that is safe to do
// during render (it is not observable state, and React's StrictMode double
// render is idempotent for it: the second render sees `wasOpen` already true
// and does not capture again).

import { useRef } from "react";

/**
 * The element that had focus before the overlay opened.
 *
 * Returns a ref whose `.current` is the element to focus back — `null` when
 * there was nothing to restore (the overlay was opened without a focused
 * element, or the opener has since been removed from the document).
 */
export function useFocusRestoreTarget(open: boolean): {
  current: HTMLElement | null;
} {
  const targetRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  if (open && !wasOpenRef.current) {
    const active = typeof document === "undefined" ? null : document.activeElement;
    targetRef.current = active instanceof HTMLElement ? active : null;
  }
  wasOpenRef.current = open;

  return targetRef;
}

/**
 * Restore the focus to the element that opened the overlay.
 *
 * Call it from Radix's `onCloseAutoFocus`. The event is prevented **only**
 * when there is something to restore — otherwise Radix's own behaviour (focus
 * the trigger, or the content) is left alone, which is the better outcome than
 * focusing a detached node.
 */
export function restoreFocus(
  event: { preventDefault: () => void },
  target: HTMLElement | null,
): void {
  if (!target) return;
  // The opener may have been unmounted while the overlay was open — a list
  // row that deleted itself, for instance. Focusing a detached node silently
  // does nothing and leaves the user at the top of the document.
  if (typeof document !== "undefined" && !document.contains(target)) return;
  event.preventDefault();
  target.focus();
}
